import React, { useRef, useState } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFile from "@mui/icons-material/UploadFile";
import { Box, Button, CircularProgress, Grid, LinearProgress } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import PaymentAPI from "api/faber/PaymentAPI";
import NormalText from "common/Typography/NormalText";

import ChangeCatalogPriceDialog from "./ChangeCatalogPriceDialog";
import UploadPriceResponseDialog from "./UploadPriceResponseDialog";


interface CatalogPriceTabProps {
    faberToken: string;
    catalogRows: GridRowDataType[];
    selectedCatalogIds: string[];
    setSelectedCatalogIds: React.Dispatch<React.SetStateAction<string[]>>;
}
interface GridRowDataType {
    id: string;
    description: string[];
    name: string;
    dataInGB: number;
    packageType: string;
    tenant: string;
    validityInDays: number;
    currency: string;
    value: string;
    price?: {
        currency: string;
        value: string;
    }
}


const CatalogPriceTab: React.FC<CatalogPriceTabProps> = ({
    faberToken,
    catalogRows,
    selectedCatalogIds,
    setSelectedCatalogIds,
}) => {
    const uploadInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
    const [responseDialogOpen, setResponseDialogOpen] = useState(false);
    const [uploadResponse, setUploadResponse] = useState({
        data: { successfulInsertions: [], failedInsertions: [] },
    });

    const priceColumns: GridColDef[] = [
        { field: "id", headerName: "Id", width: 180 },
        { field: "name", headerName: "Name", width: 200 },
        { field: "dataInGB", headerName: "Data In GB", width: 100 },
        { field: "validityInDays", headerName: "Validity In Days", width: 100 },
        {
            field: "provisionMethod.method",
            headerName: "Provision",
            width: 120,
            valueGetter: (params) => params.row.provisionMethod?.method,
        },
        {
            field: "price.currency",
            headerName: "Currency",
            width: 100,
            valueGetter: (params) => params.row.price?.currency || "--",
        },
        {
            field: "price.value",
            headerName: "Value",
            width: 100,
            valueGetter: (params) => params.row.price?.value || "--",
        },
        {
            field: "updatePrice",
            flex: 0.75,
            minWidth: 90,
            filterable: false,
            sortable: false,
            headerName: "Update Price",
            renderCell: (params: { row: GridRowDataType }) => {
                const { row } = params;
                return <ChangeCatalogPriceDialog id={row.id.toString()} />;
            },
        },
    ];

    const handleDownloadCSV = () => {
        const currencies = [
            "USD", "SGD", "GBP", "EUR", "CAD", "AUD", "KRW", "CNY",
            "MNT", "IDR", "NZD", "JPY", "INR", "MXN", "SAR", "PLN"
        ];
        const header = "catalog_id,currency,unit_amount,interval,list_price\n";
        const selectedRows = catalogRows.filter(row => selectedCatalogIds.includes(row.id));

        if (selectedRows.length === 0) {
            alert("Please select at least one item to download.");
            return;
        }

        const csvRows = selectedRows.flatMap(row =>
            currencies.map(currency => `${row.id},${currency},,0,`)
        );

        const blob = new Blob([header + csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "catalog_prices_template.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleUploadCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target?.result as string;
            const rowCount = text.trim().split(/\r?\n/).length;
            if (rowCount >= 60) {
                alert("Please upload a CSV file with fewer than 60 rows.");
                return;
            }

            setUploadedFileName(file.name);
            setUploading(true);

            const formData = new FormData();
            formData.append("file", file);

            PaymentAPI.uploadFilePriceCSV(faberToken, formData)
                .then((response) => {
                    setUploadResponse(response.data);
                    setResponseDialogOpen(true);
                })
                .catch(() => {
                    alert("Error uploading file. Please try again.");
                    setUploadResponse({ data: { successfulInsertions: [], failedInsertions: [] } });
                    setResponseDialogOpen(true);
                })
                .finally(() => setUploading(false));
        };

        reader.readAsText(file);
    };

    const handleUploadButtonClick = () => uploadInputRef.current?.click();

    return (
        <Box>
            <Box
                display="flex"
                gap={4}
                mb={3}
                sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "#fafafa",
                    boxShadow: "0px 1px 4px rgba(0,0,0,0.1)",
                }}
            >
                <input
                    type="file"
                    ref={uploadInputRef}
                    style={{ display: "none" }}
                    onChange={handleUploadCSV}
                    accept=".csv"
                />
                <Box display="flex" flexDirection="column" gap={1}>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleDownloadCSV}
                        disabled={uploading}
                        sx={{ minWidth: 180 }}
                    >
                        Download CSV
                        <DownloadIcon sx={{ ml: 1 }} />
                    </Button>
                    <span style={{ fontSize: "0.8rem", color: "#666" }}>
                        📥 Get a template for catalog prices.
                    </span>
                </Box>

                <Box display="flex" flexDirection="column" gap={1}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleUploadButtonClick}
                        disabled={uploading}
                        sx={{ minWidth: 180, display: "flex", alignItems: "center", gap: 1 }}
                    >
                        {uploading ? "Uploading..." : "Upload CSV"}
                        {uploading ? (
                            <CircularProgress size={20} sx={{ color: "white" }} />
                        ) : (
                            <UploadFile />
                        )}
                    </Button>
                    <span style={{ fontSize: "0.8rem", color: "#666" }}>
                        📌 Must contain fewer than <strong style={{ color: "red" }}>60</strong> elements.
                    </span>
                    {uploadedFileName && (
                        <span style={{ fontSize: "0.8rem", color: "green" }}>
                            ✅ Uploaded file: <strong>{uploadedFileName}</strong>
                        </span>
                    )}
                </Box>
            </Box>

            {uploading && (
                <Box mb={2}>
                    <LinearProgress />
                </Box>
            )}

            <NormalText isBold>Catalog Price</NormalText>
            <UploadPriceResponseDialog
                open={responseDialogOpen}
                onClose={() => setResponseDialogOpen(false)}
                responseData={uploadResponse}
            />

            <Grid item xs={12} height={640}>
                <DataGrid
                    loading={uploading}
                    columns={priceColumns}
                    rows={catalogRows}
                    getRowId={(row) => row.id}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    checkboxSelection
                    sortModel={[{ field: "name", sort: "asc" }]}
                    onSelectionModelChange={(newSelection) =>
                        setSelectedCatalogIds(newSelection as string[])
                    }
                    selectionModel={selectedCatalogIds}
                />
            </Grid>
        </Box>
    );
};

export default CatalogPriceTab;
